// frontend/src/components/dashboard/TopCards.js
// The "Dashboard / Top Cards" table from the spec: one row per status,
// columns Total | PSG | GPDS | Other task.
import React from 'react';
import {
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Box, Skeleton, Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { STATUS_COLORS } from '../../constants/domain';

const TopCards = ({ data, loading }) => {
    const navigate = useNavigate();
    const rows = data?.rows || [];
    const taskTypes = data?.task_types || ['PSG', 'GPDS', 'Other task'];

    const buildParams = (row, taskType) => {
        const params = new URLSearchParams();
        // A grouped row (e.g. Internal QC) covers two statuses, so only filter
        // when the row maps to exactly one.
        if (row.statuses && row.statuses.length === 1) {
            params.set('status', row.statuses[0]);
        }
        if (taskType) params.set('task_type', taskType);
        return params;
    };

    const goToParts = (row, taskType) => {
        const params = buildParams(row, taskType);
        navigate(`/parts${params.toString() ? `?${params}` : ''}`);
    };

    if (loading && rows.length === 0) {
        return (
            <Paper sx={{ p: 2 }}>
                <Skeleton variant="text" width={180} height={32} />
                {[...Array(8)].map((_, i) => <Skeleton key={i} height={38} />)}
            </Paper>
        );
    }

    const cellSx = (row) => ({
        fontWeight: row.kind === 'total' ? 700 : row.kind === 'child' ? 400 : 500,
        fontSize: row.kind === 'total' ? '1.05rem' : '0.9rem',
        py: row.kind === 'child' ? 0.5 : 1,
    });

    return (
        <Paper sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <Typography variant="h6" fontWeight={600}>Task type summary</Typography>
                <Typography variant="body2" color="text.secondary">
                    Parts by status. Click any number to open Parts Tracker.
                </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 560 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>
                                Task type
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>
                                Total
                            </TableCell>
                            {taskTypes.map(t => (
                                <TableCell key={t} align="right"
                                    sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>
                                    {t}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow
                                key={row.label}
                                hover
                                sx={{ bgcolor: row.kind === 'total' ? 'action.selected' : undefined }}
                            >
                                <TableCell sx={{ ...cellSx(row), pl: row.kind === 'child' ? 4 : 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {row.kind !== 'total' && (
                                            <Box sx={{
                                                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                                bgcolor: STATUS_COLORS[row.statuses?.[0]] || 'grey.400',
                                            }} />
                                        )}
                                        {row.label}
                                    </Box>
                                </TableCell>
                                <TableCell align="right" sx={cellSx(row)}>
                                    <Tooltip title="Open in Parts Tracker">
                                        <Box component="span"
                                            onClick={() => goToParts(row, null)}
                                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                            {row.total}
                                        </Box>
                                    </Tooltip>
                                </TableCell>
                                {taskTypes.map(t => (
                                    <TableCell key={t} align="right" sx={cellSx(row)}>
                                        <Box component="span"
                                            onClick={() => goToParts(row, t)}
                                            sx={{
                                                cursor: 'pointer',
                                                color: row[t] ? 'text.primary' : 'text.disabled',
                                                '&:hover': { textDecoration: row[t] ? 'underline' : 'none' },
                                            }}>
                                            {row[t]}
                                        </Box>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default TopCards;
